/**
 * 访问令牌轮换
 * authToken 定时刷新 + 无感续期
 */
import type { Socket } from 'socket.io-client'

export interface TokenRotationOptions {
  /** 刷新间隔（ms），默认 300000（5分钟） */
  refreshInterval?: number
  /** 提前刷新（ms），在过期前多久刷新，默认 30000 */
  refreshBefore?: number
  /** 令牌获取函数 */
  getToken: () => Promise<string>
  /** 令牌过期时间获取函数（返回时间戳） */
  getExpiry?: (token: string) => number
}

export class TokenRotation {
  private socket: Socket | null = null
  private timer: ReturnType<typeof setTimeout> | null = null
  private currentToken: string = ''
  private options: TokenRotationOptions
  private refreshCount = 0
  private lastRefresh = 0

  constructor(options: TokenRotationOptions) {
    this.options = options
  }

  /** 绑定 socket */
  bind(socket: Socket): void {
    this.socket = socket
  }

  /** 解绑 */
  unbind(): void {
    this.stop()
    this.socket = null
  }

  /** 启动自动刷新 */
  start(): void {
    this.scheduleRefresh()
  }

  /** 停止刷新 */
  stop(): void {
    if (this.timer) { clearTimeout(this.timer); this.timer = null }
  }

  /** 手动刷新 */
  async refresh(): Promise<string> {
    const newToken = await this.options.getToken()
    this.currentToken = newToken
    this.refreshCount++
    this.lastRefresh = Date.now()

    if (this.socket?.connected) {
      this.socket.emit('token:refresh', { token: newToken })
    }

    return newToken
  }

  /** 获取当前令牌 */
  getToken(): string {
    return this.currentToken
  }

  /** 获取刷新统计 */
  getStats(): { refreshCount: number; lastRefresh: number; currentToken: string } {
    return {
      refreshCount: this.refreshCount,
      lastRefresh: this.lastRefresh,
      currentToken: this.currentToken,
    }
  }

  private scheduleRefresh(): void {
    const interval = this.getNextRefreshInterval()
    this.timer = setTimeout(async () => {
      try {
        await this.refresh()
      } catch {}
      this.scheduleRefresh()
    }, interval)
  }

  private getNextRefreshInterval(): number {
    if (this.options.getExpiry && this.currentToken) {
      const expiry = this.options.getExpiry(this.currentToken)
      const remaining = expiry - Date.now()
      const refreshBefore = this.options.refreshBefore ?? 30000
      if (remaining > refreshBefore) return remaining - refreshBefore
    }
    return this.options.refreshInterval ?? 300000
  }
}
